import React from 'react';
import { Candidate } from '../pages/CandidateMatches';

interface CandidateMatchCardProps {
    candidate: Candidate;
    animationDelay?: number;
    onClick?: (candidate: Candidate) => void;
}

const CandidateMatchCard: React.FC<CandidateMatchCardProps> = ({ 
    candidate, 
    animationDelay = 0,
    onClick 
}) => {
    const getMatchScoreColor = (score: number) => {
        if (score >= 90) return '#10B981'; // Green for 90%+
        if (score >= 80) return '#3B82F6'; // Blue for 80%+
        if (score >= 70) return '#F59E0B'; // Yellow for 70%+
        return '#EF4444'; // Red for below 70%
    };

    const getCountryFlag = (countryCode: string) => {
        // You can replace this with actual flag images or use a flag icon library
        const flags: { [key: string]: string } = {
            'AE': '🇦🇪',
            'DE': '🇩🇪',
            'IN': '🇮🇳',
            'US': '🇺🇸',
            'UK': '🇬🇧',
            'CA': '🇨🇦'
        };
        return flags[countryCode] || '🌍';
    };

    const handleCardClick = () => {
        if (onClick) {
            onClick(candidate);
        }
    };

    return (
        <div 
            className="animate-fade-in-up cursor-pointer"
            style={{ 
                animationDelay: `${animationDelay}s`,
                background: '#FFFFFF',
                borderRadius: '20px',
                padding: '28px',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                position: 'relative',
                minHeight: '280px'
            }}
            onClick={handleCardClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0px 16px 48px rgba(0, 0, 0, 0.16)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0px 8px 32px rgba(0, 0, 0, 0.12)';
            }}
        >
            {/* Match Score Badge */}
            <div 
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    background: getMatchScoreColor(candidate.matchScore),
                    color: '#FFFFFF',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    fontFamily: 'Manrope'
                }}
            >
                Match Score {candidate.matchScore}%
            </div>

            {/* Profile Photo */}
            <div 
                style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    margin: '0 auto 20px',
                    border: '3px solid #F3F4F6'
                }}
            >
                <img
                    src={candidate.avatar}
                    alt={candidate.name}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                    onError={(e) => {
                        // Fallback if image fails to load
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${candidate.name}&background=2563EB&color=fff&size=80`;
                    }}
                />
            </div>

            {/* Candidate Info */}
            <div style={{ textAlign: 'center' }}>
                {/* Name */}
                <h3 
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 600,
                        fontSize: '20px',
                        color: '#1F2937',
                        margin: '0 0 8px 0'
                    }}
                >
                    {candidate.name}
                </h3>

                {/* Job Title */}
                <p 
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 500,
                        fontSize: '16px',
                        color: '#2563EB',
                        margin: '0 0 12px 0'
                    }}
                >
                    {candidate.jobTitle}
                </p>

                {/* Experience/Details */}
                <p 
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: '0 0 16px 0',
                        lineHeight: '20px'
                    }}
                >
                    {candidate.experience}
                </p>

                {/* Location */}
                <div 
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontFamily: 'Manrope',
                        fontSize: '14px',
                        color: '#6B7280'
                    }}
                >
                    <span style={{ fontSize: '18px' }}>
                        {getCountryFlag(candidate.countryCode)}
                    </span>
                    <span>{candidate.location}</span>
                </div>
            </div>
        </div>
    );
};

export default CandidateMatchCard;
